import {
  aiChatbotResendButtonClickedSrc4Evid1173,
  aiChatbotResendButtonPresentedSrc4Evid1172,
  breadcrumbsScroll,
  chatbotClickLink,
  chatbotClickOnSolution,
  chatbotDisplayWarning,
  chatbotLoadChatbot,
  chatbotNotifications,
  chatbotStartOver,
  chatbotStatusBannerStatusBannerClicked,
  chatbotStatusBannerStatusBannerDismissed,
  chatbotStatusBannerStatusBannerShowed,
  schedulerUserChoosesADay,
  schedulerUserClickedOnASlot,
  schedulerUserSearchedADifferentMonth,
  widgetContentInteraction,
  widgetEmptySearchResults,
  widgetPageEngagement,
  widgetPresentedContent,
  widgetScrolledToBottom,
  wixContactClickOnALinkInsideASolution,
  wixContactClickOnTheActionButton,
} from '@wix/bi-logger-customer-care-data/v2';
import {
  aiChatbotResendButtonClickedSrc4Evid1173Params,
  aiChatbotResendButtonPresentedSrc4Evid1172Params,
  breadcrumbsScrollParams,
  chatbotClickLinkParams,
  chatbotClickOnSolutionParams,
  chatbotDisplayWarningParams,
  chatbotLoadChatbotParams,
  chatbotNotificationsParams,
  chatbotStartOverParams,
  chatbotStatusBannerStatusBannerClickedParams,
  chatbotStatusBannerStatusBannerDismissedParams,
  chatbotStatusBannerStatusBannerShowedParams,
  schedulerUserChoosesADayParams,
  schedulerUserClickedOnASlotParams,
  schedulerUserSearchedADifferentMonthParams,
  widgetContentInteractionParams,
  widgetEmptySearchResultsParams,
  widgetPageEngagementParams,
  widgetPresentedContentParams,
  widgetScrolledToBottomParams,
  wixContactClickOnALinkInsideASolutionParams,
  wixContactClickOnTheActionButtonParams,
} from '@wix/bi-logger-customer-care-data/v2/types';
import { factory } from '@wix/web-bi-logger';

import { parentUrl, referral, referralUrl } from './constants';
import { RequiredWithUndefined } from './types';
import { createOnError } from './utils';

const defaultBiFields = {
  bot_id: '717b0e2d-a564-4ef7-b965-481b1983133b',
  provider: 'answers',
  referral_url: referralUrl,
  parent_url: parentUrl,
  referral,
};

type BiParams<T> = Readonly<
  // __cookies_prefix__wixLanguage - tech solution related to migration to catalog v2. Do not need to send it for now.
  Omit<RequiredWithUndefined<T>, keyof typeof defaultBiFields | '__cookies_prefix__wixLanguage'>
>;

class BiLogger {
  private readonly _answerLogger = factory({ endpoint: 'ans_bi' }).updateDefaults(defaultBiFields).logger();
  private readonly _chatbotLogger = factory({ endpoint: 'chatbot' }).updateDefaults(defaultBiFields).logger();

  // 574
  wixContactClickOnTheActionButton(params: BiParams<wixContactClickOnTheActionButtonParams>): void {
    this._answerLogger.report(wixContactClickOnTheActionButton(params)).catch(createOnError(574, params));
  }

  // 575
  wixContactClickOnALinkInsideASolution(params: BiParams<wixContactClickOnALinkInsideASolutionParams>): void {
    this._answerLogger
      .report(wixContactClickOnALinkInsideASolution(params))
      .catch(createOnError(575, params));
  }

  // 1051
  chatbotLoadChatbot(params: BiParams<chatbotLoadChatbotParams>): void {
    this._chatbotLogger.report(chatbotLoadChatbot(params)).catch(createOnError(1051, params));
  }

  // 1054
  chatbotStartOver(params: BiParams<chatbotStartOverParams>): void {
    this._chatbotLogger.report(chatbotStartOver(params)).catch(createOnError(1054, params));
  }

  // 1088
  chatbotClickLink(params: BiParams<chatbotClickLinkParams>): void {
    this._chatbotLogger.report(chatbotClickLink(params)).catch(createOnError(1088, params));
  }

  // 1119
  widgetPresentedContent(params: BiParams<widgetPresentedContentParams>): void {
    this._chatbotLogger.report(widgetPresentedContent(params)).catch(createOnError(1119, params));
  }

  // 1120
  widgetContentInteraction(params: BiParams<widgetContentInteractionParams>): void {
    this._chatbotLogger.report(widgetContentInteraction(params)).catch(createOnError(1120, params));
  }

  // 1121
  widgetPageEngagement(params: BiParams<widgetPageEngagementParams>): void {
    this._chatbotLogger.report(widgetPageEngagement(params)).catch(createOnError(1121, params));
  }

  // 1123
  chatbotDisplayWarning(params: BiParams<chatbotDisplayWarningParams>): void {
    this._chatbotLogger.report(chatbotDisplayWarning(params)).catch(createOnError(1123, params));
  }

  // 1127
  chatbotStatusBannerStatusBannerShowed(params: BiParams<chatbotStatusBannerStatusBannerShowedParams>): void {
    this._chatbotLogger
      .report(chatbotStatusBannerStatusBannerShowed(params))
      .catch(createOnError(1127, params));
  }

  // 1128
  chatbotStatusBannerStatusBannerClicked(
    params: BiParams<chatbotStatusBannerStatusBannerClickedParams>,
  ): void {
    this._chatbotLogger
      .report(chatbotStatusBannerStatusBannerClicked(params))
      .catch(createOnError(1128, params));
  }

  // 1129
  chatbotStatusBannerStatusBannerDismissed(
    params: BiParams<chatbotStatusBannerStatusBannerDismissedParams>,
  ): void {
    this._chatbotLogger
      .report(chatbotStatusBannerStatusBannerDismissed(params))
      .catch(createOnError(1129, params));
  }

  // 1132
  widgetScrolledToBottom(params: BiParams<widgetScrolledToBottomParams>): void {
    this._chatbotLogger.report(widgetScrolledToBottom(params)).catch(createOnError(1132, params));
  }

  // 1151
  schedulerUserSearchedADifferentMonth(params: BiParams<schedulerUserSearchedADifferentMonthParams>): void {
    this._chatbotLogger
      .report(schedulerUserSearchedADifferentMonth(params))
      .catch(createOnError(1151, params));
  }

  // 1152
  schedulerUserChoosesADay(params: BiParams<schedulerUserChoosesADayParams>): void {
    this._chatbotLogger.report(schedulerUserChoosesADay(params)).catch(createOnError(1152, params));
  }

  // 1153
  schedulerUserClickedOnASlot(params: BiParams<schedulerUserClickedOnASlotParams>): void {
    this._chatbotLogger.report(schedulerUserClickedOnASlot(params)).catch(createOnError(1153, params));
  }

  // 1159
  chatbotClickOnSolution(params: BiParams<chatbotClickOnSolutionParams>): void {
    this._chatbotLogger.report(chatbotClickOnSolution(params)).catch(createOnError(1159, params));
  }

  // 1160
  widgetEmptySearchResults(params: BiParams<widgetEmptySearchResultsParams>): void {
    this._chatbotLogger.report(widgetEmptySearchResults(params)).catch(createOnError(1160, params));
  }

  // 1161
  breadcrumbsScroll(params: BiParams<breadcrumbsScrollParams>): void {
    this._chatbotLogger.report(breadcrumbsScroll(params)).catch(createOnError(1161, params));
  }

  // 1162
  chatbotNotifications(params: BiParams<chatbotNotificationsParams>): void {
    this._chatbotLogger.report(chatbotNotifications(params)).catch(createOnError(1162, params));
  }

  // 1172
  resendButtonAppear(params: BiParams<aiChatbotResendButtonPresentedSrc4Evid1172Params>): void {
    this._chatbotLogger
      .report(aiChatbotResendButtonPresentedSrc4Evid1172(params))
      .catch(createOnError(1172, params));
  }

  // 1173
  resendButtonClicked(params: BiParams<aiChatbotResendButtonClickedSrc4Evid1173Params>): void {
    this._chatbotLogger
      .report(aiChatbotResendButtonClickedSrc4Evid1173(params))
      .catch(createOnError(1173, params));
  }
}

export const biLogger = new BiLogger();
